import React, { useEffect, useState } from 'react';
import Modal from '../../Modal';
import { useApi } from '../../../lib/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EXTRA_CC = 4;
const MAX_EMAIL_LENGTH = 254; // RFC 5321 max total length for an email address

// Mail Control modal for one agreement, reached via the gear icon on the
// Edit Agreement form. Holds up to 4 extra CC recipients that get CC'd on
// the "all services completed & approved" mail sent to the client — there
// are no per-type toggles here (unlike Projects), just the CC list.
export default function AgreementMailSettingsModal({ open, onClose, appId, agreement, canEdit }) {
  const api = useApi();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [newCcEmail, setNewCcEmail] = useState('');
  const [ccSaving, setCcSaving] = useState(false);
  const [ccError, setCcError] = useState('');

  useEffect(() => {
    if (!open || !agreement) return;
    setLoading(true);
    setError('');
    api.getAgreementMailSettings(appId, agreement.id)
      .then(setSettings)
      .catch((err) => setError(err.response?.data?.error || err.message || 'Failed to load mail settings'))
      .finally(() => setLoading(false));
  }, [open, agreement, appId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) { setNewCcEmail(''); setCcError(''); }
  }, [open]);

  async function saveExtraCc(nextList) {
    const previous = settings;
    setSettings((s) => ({ ...s, mail_extra_cc: nextList }));
    setCcSaving(true);
    setCcError('');
    try {
      const updated = await api.updateAgreementMailSettings(appId, agreement.id, { mail_extra_cc: nextList });
      setSettings(updated);
      return true;
    } catch (err) {
      setSettings(previous);
      setCcError(err.response?.data?.error || err.message || 'Failed to update CC recipients');
      return false;
    } finally {
      setCcSaving(false);
    }
  }

  async function handleAddCc(e) {
    e.preventDefault();
    const email = newCcEmail.trim().toLowerCase();
    setCcError('');
    if (!email) return;
    if (email.length > MAX_EMAIL_LENGTH) {
      setCcError(`That email is too long (max ${MAX_EMAIL_LENGTH} characters)`);
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setCcError('Enter a valid email address, e.g. name@company.com');
      return;
    }
    const current = settings.mail_extra_cc || [];
    if (current.length >= MAX_EXTRA_CC) { setCcError(`You can add at most ${MAX_EXTRA_CC} extra CC recipients`); return; }
    if (current.includes(email)) { setCcError('That email is already in the list'); return; }
    const ok = await saveExtraCc([...current, email]);
    if (ok) setNewCcEmail('');
  }

  async function handleRemoveCc(email) {
    const current = settings.mail_extra_cc || [];
    await saveExtraCc(current.filter((e) => e !== email));
  }

  if (!agreement) return null;

  const extraCc = settings?.mail_extra_cc || [];
  const title = agreement.agreement_name || agreement.agreement_number;

  return (
    <Modal open={open} onClose={onClose} title={`Mail Control — ${title}`} size="sm">
      {loading || !settings ? (
        <div className="tl-loading"><span className="tl-spinner" /> Loading…</div>
      ) : (
        <>
          <p style={{ fontSize: 12, color: 'var(--fg-dim)', marginTop: 0, marginBottom: 16, lineHeight: 1.5 }}>
            When every service on this agreement is completed and approved, GLOS emails the client
            with the agreement details and the completed service list.
            {!canEdit && ' Only the agreement owner or an admin can change who else gets CC\'d.'}
          </p>

          {error && <div className="form-error" style={{ marginBottom: 14 }}>{error}</div>}

          <div>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)' }}>Notify</span>
            <p style={{ fontSize: 12, color: 'var(--fg-dim)', margin: '4px 0 12px', lineHeight: 1.5 }}>
              Up to {MAX_EXTRA_CC} additional email addresses to CC on that mail, alongside the client,
              relationship manager and app owner.
            </p>

            {extraCc.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: canEdit ? 12 : 0 }}>
                {extraCc.map((email) => (
                  <div key={email} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '5px 10px', background: 'var(--bg-3, rgba(0,0,0,0.03))',
                    border: '1px solid var(--border)', borderRadius: 7, fontSize: 12.5,
                  }}>
                    <span style={{ color: 'var(--fg)' }}>{email}</span>
                    {canEdit && (
                      <button
                        onClick={() => handleRemoveCc(email)}
                        disabled={ccSaving}
                        title="Remove"
                        style={{
                          background: 'none', border: 'none', cursor: ccSaving ? 'wait' : 'pointer',
                          color: 'var(--fg-mute, #94a3b8)', fontSize: 13, padding: '0 2px', lineHeight: 1,
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {canEdit && extraCc.length < MAX_EXTRA_CC && (
              <form onSubmit={handleAddCc} style={{ display: 'flex', gap: 8 }}>
                <input
                  type="email"
                  className="form-input"
                  placeholder="name@company.com"
                  value={newCcEmail}
                  onChange={(e) => setNewCcEmail(e.target.value)}
                  disabled={ccSaving}
                  maxLength={MAX_EMAIL_LENGTH}
                  style={{ margin: 0, flex: 1, fontSize: 12.5 }}
                />
                <button
                  type="submit"
                  className="btn-secondary"
                  disabled={ccSaving || !newCcEmail.trim()}
                  style={{ padding: '4px 14px', fontSize: 12.5, flexShrink: 0 }}
                >
                  {ccSaving ? '…' : '+ Add'}
                </button>
              </form>
            )}

            {ccError && <div className="form-error" style={{ marginTop: 10 }}>{ccError}</div>}
          </div>
        </>
      )}
    </Modal>
  );
}
